import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { FormikProps } from "formik";
import { Stack } from "components/layout/reusableLayouts";
import { titleCase } from "title-case";
import { useMask } from "@react-input/mask";
import { Select, TextField } from "@outpave/ds";
import { fieldErrorText } from "utils/formikFieldError";
import { relationshipLabelKey } from "utils/contacts/relationship";
import MerchantSearchAutoComplete, {
  Merchant,
  merchantForDomain,
} from "components/common/MerchantSearchAutoComplete";
import AddressField from "components/common/address/AddressField";
import AddPaymentDetailsToContact from "./AddPaymentDetailsToContact";
import {
  EMPTY_CONTACT_ADDRESS,
  FormikContact,
} from "../../../hooks/useContactData";
import RelationshipType from "../../../enums/RelationshipType";
import MerchantSearchType from "../../../enums/MerchantSearchType";
import FormikCustomFields from "components/common/FormikCustomFields";
import { FormikCustomField } from "hooks/customFields.types";

interface HelperTextProps {
  errorText: string;
  onClick?: () => void;
}

const HelperText: React.FC<HelperTextProps> = ({ errorText, onClick }) => {
  return (
    <Typography
      variant="caption"
      onClick={onClick}
      sx={{
        cursor: onClick ? "pointer" : "default",
        color: "var(--color-text-danger)",
      }}
    >
      {errorText}
    </Typography>
  );
};

interface EditBusinessContactProps {
  contactFormik: FormikProps<FormikContact>;
  customFieldSettings?: FormikCustomField[];
  errorContactFieldAlreadyExists?: string;
  popoverExistingContactRef?: React.RefObject<HTMLDivElement>;
  isExternal?: boolean;
  customPaymentDetailsDescription?: string;
  setOpenAddressDrawer: (open: boolean) => void;
  setOpenExistingContactPopover?: (open: boolean) => void;
}

const EditBusinessContact: React.FC<EditBusinessContactProps> = ({
  contactFormik,
  customFieldSettings,
  errorContactFieldAlreadyExists = "",
  popoverExistingContactRef = React.createRef<HTMLDivElement>(),
  isExternal = false,
  customPaymentDetailsDescription = "",
  setOpenAddressDrawer,
  setOpenExistingContactPopover,
}) => {
  const { t } = useTranslation();

  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(
    null,
  );

  const onSetSelectedMerchant = (merchant: Merchant | null) => {
    if (!merchant) {
      setSelectedMerchant(null);
      contactFormik.setFieldValue("businessName", "");
      contactFormik.setFieldValue("website", "");
      return;
    }

    setSelectedMerchant(merchant);

    if (merchant.name && !contactFormik.values.businessName) {
      contactFormik.setFieldValue(
        "businessName",
        titleCase(merchant.name.toLowerCase()),
      );
    }
    contactFormik.setFieldValue("website", merchant.url.toLowerCase());
  };

  const handleClickErrorContactAlreadyExists = () => {
    setOpenExistingContactPopover && setOpenExistingContactPopover(true);
  };

  const phoneMaskRef = useMask({
    mask: "___-___-____",
    replacement: { _: /\d/ },
  });

  return (
    <>
      {contactFormik.values.contactType && (
        <Stack gap="var(--space-6)">
          {!isExternal && (
            <div ref={popoverExistingContactRef}>
              <MerchantSearchAutoComplete
                title={t("Website")}
                required
                placeholder={t("Search Business Domain")}
                merchant={
                  selectedMerchant ??
                  (contactFormik.values.website
                    ? merchantForDomain(contactFormik.values.website)
                    : null)
                }
                onSelectMerchant={onSetSelectedMerchant}
                fullWidth
                allowUseSearchValue
                type={MerchantSearchType.brand}
                errorText={
                  contactFormik.touched.website &&
                  (contactFormik.errors.website ||
                    errorContactFieldAlreadyExists) ? (
                    <HelperText
                      errorText={
                        contactFormik.errors.website ||
                        errorContactFieldAlreadyExists
                      }
                      onClick={
                        errorContactFieldAlreadyExists
                          ? handleClickErrorContactAlreadyExists
                          : undefined
                      }
                    />
                  ) : null
                }
                handleBlur={() => {
                  contactFormik.setTouched({
                    ...contactFormik.touched,
                    website: true,
                  });
                }}
              />
            </div>
          )}

          {isExternal && (
            <TextField
              fullWidth
              required
              disabled
              label={t("Business Domain")}
              name="businessName"
              value={contactFormik.values.website}
            />
          )}

          <TextField
            fullWidth
            required
            label={t("Business Name")}
            name="businessName"
            value={contactFormik.values.businessName}
            onChange={contactFormik.handleChange}
            onBlur={contactFormik.handleBlur}
            errorText={fieldErrorText(contactFormik, "businessName")}
          />

          <TextField
            fullWidth
            required
            label={t("Email")}
            name="email"
            value={contactFormik.values.email}
            onChange={contactFormik.handleChange}
            onBlur={contactFormik.handleBlur}
            errorText={fieldErrorText(contactFormik, "email")}
          />

          <TextField
            fullWidth
            label={t("Phone")}
            name="phone"
            value={contactFormik.values.phone}
            onChange={contactFormik.handleChange}
            onBlur={contactFormik.handleBlur}
            errorText={fieldErrorText(contactFormik, "phone")}
            // `ref`, not `inputRef`: the DS field sets `inputRef` from its own
            // forwarded ref after spreading `...rest`, so an `inputRef` here is
            // discarded and the mask silently stops working.
            ref={phoneMaskRef}
          />

          <AddressField
            label={t("Address")}
            name="address"
            address={contactFormik.values.address}
            onOpen={() => setOpenAddressDrawer(true)}
            onClear={() =>
              contactFormik.setFieldValue("address", EMPTY_CONTACT_ADDRESS)
            }
            errorText={fieldErrorText(contactFormik, "address.addressName")}
          />

          {!isExternal && (
            <Select
              fullWidth
              required
              // relationshipType is RelationshipTypeEnum! and updateContact
              // drops a falsy one from the mutation, so a cleared value leaves
              // the record alone while the form shows empty.
              clearable={false}
              label={t("Relationship Type")}
              value={contactFormik.values.relationshipType || null}
              options={Object.values(RelationshipType).map((type) => ({
                id: type,
                label: t(relationshipLabelKey(type) ?? type),
              }))}
              onChange={(value) =>
                value && contactFormik.setFieldValue("relationshipType", value)
              }
              errorText={fieldErrorText(contactFormik, "relationshipType")}
              helperText={t("Define this contact's role with your business")}
            />
          )}

          {!isExternal && (
            <FormikCustomFields
              settings={customFieldSettings}
              formik={contactFormik}
              variant="flat"
            />
          )}

          <AddPaymentDetailsToContact
            contactFormik={contactFormik}
            {...(customPaymentDetailsDescription && {
              customDescription: customPaymentDetailsDescription,
            })}
          />
        </Stack>
      )}
    </>
  );
};

export default EditBusinessContact;
